import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../services/supabaseClient';

const FALLBACK_NEXT_PATH = '/assessment';
const MAX_COMPANY_NAME_LENGTH = 200;

const normalizeNextPath = (rawNext: string | null): string => {
  const candidate = (rawNext || '').trim();
  if (!candidate) return FALLBACK_NEXT_PATH;
  if (!candidate.startsWith('/')) return FALLBACK_NEXT_PATH;
  if (candidate.startsWith('//')) return FALLBACK_NEXT_PATH;
  return candidate;
};

const redirectToLogin = (reason: string) => {
  window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
};

export default function AuthConfirm() {
  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get('token_hash');
      const typeParam = params.get('type');
      const nextPath = normalizeNextPath(params.get('next'));
      const rawCompanyName = (params.get('companyName') || '').trim();
      const companyName =
        rawCompanyName && rawCompanyName.length <= MAX_COMPANY_NAME_LENGTH
          ? rawCompanyName
          : '';

      if (companyName) {
        localStorage.setItem('pending_company_name', companyName);
      }

      Sentry.addBreadcrumb({
        category: 'auth.confirm',
        message: 'Auth confirm route entered',
        level: 'info',
        data: {
          hasTokenHash: Boolean(tokenHash),
          type: typeParam || 'missing',
          nextPath,
          hasCompanyName: Boolean(companyName),
        },
      });

      if (!tokenHash || typeParam !== 'email') {
        redirectToLogin('invalid-confirm-link');
        return;
      }

      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'email',
      });

      if (error) {
        Sentry.captureException(error, {
          tags: { area: 'auth-confirm' },
          extra: { nextPath },
        });
        redirectToLogin('invalid-or-expired-link');
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const refreshToken = sessionData.session?.refresh_token;

      if (!accessToken) {
        redirectToLogin('invalid-or-expired-link');
        return;
      }

      const hashParams = new URLSearchParams();
      hashParams.set('access_token', accessToken);
      if (refreshToken) {
        hashParams.set('refresh_token', refreshToken);
      }

      const redirectUrl = new URL(nextPath, window.location.origin);
      if (companyName && redirectUrl.pathname === '/assessment') {
        redirectUrl.searchParams.set('companyName', companyName);
      }

      window.location.replace(`${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}#${hashParams.toString()}`);
    };

    void run();
  }, []);

  return null;
}
